import webStomp, { Message } from "webstomp-client";
import { WebSocketMessage, WebSocketMessageAction } from "@/types/websocket";
import { Leg as LegResponse } from "@/types/leg";
import { getCookie } from "@/use/useCookie";
import { TP_COOKIE_NAME, DISPATCHER_BASE_URL } from "@/env_config";
import Sock from "sockjs-client";
import { ref } from "vue";
import { FleetAction } from "@/types/action";
import { Trailer } from "@/types/fleet";
import { Chat } from "@/types/chat";
import { useChatStore } from "@/store/chat/ChatStore";
import { useBrickStore } from "@/store/brick/BrickStore";
import { useFleetStore } from "@/store/fleet/FleetStore";
import { useBoardStore } from "@/store/board/BoardStore";

function shouldIgnoreDuplicateUpdate(id: string): boolean {
  const ignorableBrickId = useBrickStore().ignorableBrickId;
  if (id === ignorableBrickId) {
    useBrickStore().ignorableBrickId = null;
    return true;
  }
  return false;
}

export default function () {
  let retryCount = 0;
  let keepaliveInterval: number;
  const stompClient: any = ref(undefined);

  const onSocketMessage = (socketMessage: Message): void => {
    const message: WebSocketMessage = JSON.parse(socketMessage.body);

    if (message.messageType === "FLEET_ACTION") {
      const action = message.messageContent as FleetAction;

      const shouldIgnoreActionUpdate = shouldIgnoreDuplicateUpdate(action.id);
      if (shouldIgnoreActionUpdate) return;

      if (message.messageAction === WebSocketMessageAction.UPDATE) {
        useBrickStore().updateFleetActions({ actions: [action], viaSocket: true });
      } else if (message.messageAction === WebSocketMessageAction.DELETE) {
        useBrickStore().removeFleetActions([action.id]);
      }
      return;
    }

    if (message.messageType === "LEG") {
      const leg = message.messageContent as LegResponse;

      const shouldIgnoreActionUpdate = shouldIgnoreDuplicateUpdate(leg.id);
      if (shouldIgnoreActionUpdate) return;

      if (message.messageAction === WebSocketMessageAction.UPDATE) {
        useBrickStore().updateLegs({ legs: [leg], viaSocket: true });
      } else if (message.messageAction === WebSocketMessageAction.DELETE) {
        useBrickStore().deleteLegs([leg.id]);
      }
    }

    if (message.messageType === "TRAILER") {
      const trailer = message.messageContent as Trailer;
      if (message.messageAction === WebSocketMessageAction.UPDATE) {
        useFleetStore().updateTrailer(trailer);
      } else if (message.messageAction === WebSocketMessageAction.DELETE) {
        useFleetStore().removeTrailer(trailer.id);
      }
    }

    if (message.messageType === "CHAT_UPDATE") {
      const chat = message.messageContent as Chat;
      if (message.messageAction === WebSocketMessageAction.UPDATE) {
        useChatStore().chatUpdate(chat);
      }
    }

    if (message.messageType === "CHAT_READ") {
      const chat = message.messageContent as Chat;
      if (message.messageAction === WebSocketMessageAction.UPDATE) {
        useChatStore().chatRead(chat);
      }
    }
  };

  const getSocketRetryInterval = retryCount => {
    return retryCount * 1000;
  };

  const isWebSocketConnected = (): boolean => {
    return typeof stompClient.value !== "undefined" && stompClient.value.connected;
  };

  const keepAlive = (): void => {
    if (isWebSocketConnected()) {
      stompClient.value!.send("/app/ping", "PING");
    }
  };

  const resetKeepAliveInterval = (): void => {
    if (typeof keepaliveInterval !== "undefined") {
      clearInterval(keepaliveInterval);
    }
  };

  const disconnectWebSocket = (): void => {
    stompClient.value.disconnect();
    resetKeepAliveInterval();
  };

  const initWebSocket = (rangeFrom: string, rangeTo: string) => {
    if (isWebSocketConnected()) {
      disconnectWebSocket();
    }

    const token = getCookie(TP_COOKIE_NAME);
    stompClient.value = webStomp.over(new Sock(`${DISPATCHER_BASE_URL}/updates-ws?tpToken=${token}`), {
      heartbeat: { incoming: 60000, outgoing: 60000 },
      debug: false
    });

    stompClient.value.connect(
      {
        "x-authorization": token,
        "x-time-range": `${rangeFrom}/${rangeTo}`
      },
      () => {
        stompClient.value!.subscribe("/user/queue/updates", onSocketMessage);
        retryCount = 0;
      },
      () => {
        console.log("WebSocket connection lost...");

        if (isWebSocketConnected()) {
          disconnectWebSocket();
        }

        if (useBoardStore().useNewWebsocketLogic) {
          useBoardStore().showLostConnectionDialog = true;
        } else {
          setTimeout(() => initWebSocket(rangeFrom, rangeTo), getSocketRetryInterval(retryCount++));
        }
      }
    );

    keepaliveInterval = setInterval(() => keepAlive(), 30000);
  };

  return {
    initWebSocket
  };
}
